import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Box, Button, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import useSalesforceReports from 'hooks/useSalesforceReports';
import ReportList from '../components/AlertCenter/ReportList';

const SelectReportForAlert = () => {
	const navigate = useNavigate();

	const { reports, loading, error, totalCount } = useSalesforceReports();
	const [searchQuery, setSearchQuery] = useState('');

	const filteredReports = useMemo(() => {
		if (!searchQuery) return reports;
		const query = searchQuery.trim().toLowerCase();
		return reports.filter(
			(report) =>
				report.name?.toLowerCase().includes(query) ||
				report.folder_name?.toLowerCase().includes(query)
		);
	}, [reports, searchQuery]);

	// Take user to report analysis page after report selection
	const handleSelectedReport = useCallback(
		(report) => {
			navigate(`analysis/${report.id}`, {
				state: { reportName: report.name },
			});
		},
		[navigate]
	);

	return (
		<Box
			sx={{
				flex: 1,
				minWidth: 0,
				overflowY: 'auto',
				backgroundColor: 'background.default',
				padding: '12px 20px',
				display: 'flex',
				flexDirection: 'column',
				gap: '14px',
			}}
		>
			<Box>
				<Button
					startIcon={<ArrowBackIcon />}
					onClick={() => navigate('..')} // Return to parent route
				>
					Alerts
				</Button>
			</Box>

			{/* Header */}
			<Box>
				<Typography sx={{ fontSize: '17px', fontWeight: 700 }}>
					New Alert - Pick a Report
				</Typography>
				<Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
					Choose the report to watch
				</Typography>
			</Box>

			{/* Report list Container*/}
			<Box
				sx={{
					flex: 1,
					minHeight: 0,
				}}
			>
				{error ? (
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							height: '100%',
							border: '0.5px solid',
							borderColor: 'border.light',
							borderRadius: '8px',
						}}
					>
						<Typography
							sx={{
								fontSize: '12px',
								color: 'text.secondary',
								textAlign: 'center',
								px: 2,
							}}
						>
							{error === 'not_connected'
								? 'Connect your CRM to view reports here.'
								: error === 'reconnect_required'
									? "Connection expired. Please remove and reconnect your CRM from Launchpad's Connections page."
									: 'Unable to load reports. Please try again later.'}
						</Typography>
					</Box>
				) : (
					<ReportList
						reports={filteredReports}
						loading={loading}
						searchQuery={searchQuery}
						setSearchQuery={setSearchQuery}
						handleSelectedReport={handleSelectedReport}
						totalCount={totalCount}
					/>
				)}
			</Box>
		</Box>
	);
};

export default SelectReportForAlert;
